import React from 'react';
import * as S from '../style';
import {
  Option,
  DestinationType,
  AssetType,
  StreamMetadataDetails,
} from '../types';
import { AssetUpload } from './AssetUpload';
import CreatableSingleSelectDropdown from 'containers/NewCreativePreview/components/Filter Modal/creatableSingleSelect';
import { StreamMetadataFields } from './StreamMetadataFields';

interface DestinationFieldsProps {
  destinationType: DestinationType | null;
  selectedAppId: Option | null;
  contentTargetValue: string;
  destinationURL: string;
  destinationAsset: File | null;
  destinationPreview: string | null;
  destinationDimensions: {
    width?: number;
    height?: number;
    duration?: number;
  } | null;
  loadAppOptions: (
    inputValue: string,
    callback: (options: Option[]) => void,
  ) => void;
  isAppFetching: boolean;
  isAppLoading: boolean;
  onAppIdChange: (value: Option | null) => void;
  onContentTargetChange: (value: string) => void;
  onDestinationURLChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDestinationFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    type: AssetType,
  ) => void;
  onDestinationDrop: (
    e: React.DragEvent<HTMLDivElement>,
    type: AssetType,
  ) => void;
  onDestinationDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDestinationRemove: () => void;
  setIsOpen: (value: boolean) => void;
  appNameOption: Option[];
  streamMetadata: StreamMetadataDetails | null;
  streamMetadataError: string;
  streamMetadataLoading: boolean;
  streamUrl: string;
  onStreamDurationChange: (value: string) => void;
  onStreamUrlBlur: () => void;
  onStreamUrlChange: (value: string) => void;
}

export const DestinationFields: React.FC<DestinationFieldsProps> = ({
  destinationType,
  selectedAppId,
  contentTargetValue,
  destinationURL,
  destinationAsset,
  destinationPreview,
  destinationDimensions,
  loadAppOptions,
  isAppFetching,
  isAppLoading,
  onAppIdChange,
  onContentTargetChange,
  onDestinationURLChange,
  onDestinationFileUpload,
  onDestinationDrop,
  onDestinationDragOver,
  onDestinationRemove,
  setIsOpen,
  appNameOption,
  streamMetadata,
  streamMetadataError,
  streamMetadataLoading,
  streamUrl,
  onStreamDurationChange,
  onStreamUrlBlur,
  onStreamUrlChange,
}) => {
  if (!destinationType) return null;
  return (
    <>
      {/* App */}
      {destinationType === 'App' && (
        <S.FormRow>
          <S.FormGroup>
            <S.Label>App Group Name</S.Label>
            <div style={{ fontSize: 13, width: 500 }}>
              <CreatableSingleSelectDropdown
                placeholder="Type to search or create apps..."
                value={selectedAppId}
                onChange={onAppIdChange}
                loadOptions={loadAppOptions}
                defaultOptions={appNameOption}
                isLoading={isAppFetching || isAppLoading}
                setIsOpen={setIsOpen}
                formatCreateLabel={input => `Add new: ${input}`}
              />
            </div>
          </S.FormGroup>
        </S.FormRow>
      )}
      {/* Deeplink */}
      {destinationType === 'Deeplink' && (
        <S.FormRow>
          <S.FormGroup>
            <S.Label>App Group Name</S.Label>
            <div style={{ fontSize: 13, width: 500 }}>
              <CreatableSingleSelectDropdown
                placeholder="Type to search or create apps..."
                value={selectedAppId}
                onChange={onAppIdChange}
                loadOptions={loadAppOptions}
                defaultOptions={appNameOption}
                isLoading={isAppFetching || isAppLoading}
                setIsOpen={setIsOpen}
                formatCreateLabel={input => `Add new: ${input}`}
              />
            </div>
          </S.FormGroup>

          <S.FormGroup>
            <S.Label>Content Target</S.Label>
            <S.Input
              value={contentTargetValue}
              onChange={e => onContentTargetChange(e.target.value)}
            />
          </S.FormGroup>
        </S.FormRow>
      )}

      {/* Web / WebFS */}
      {(destinationType === 'Web' || destinationType === 'WebFS') && (
        <S.FormRow>
          <S.FormGroup>
            <S.Label>Destination URL</S.Label>
            <S.Input value={destinationURL} onChange={onDestinationURLChange} />
          </S.FormGroup>
        </S.FormRow>
      )}

      {/* Image / Video */}
      {(destinationType === 'Image' || destinationType === 'Video') && (
        <S.FormRow>
          <S.FormGroup>
            <S.AssetLabel>Destination Asset</S.AssetLabel>
            <S.SubText>Drag files or browse</S.SubText>
            <AssetUpload
              type="destination"
              file={destinationAsset}
              preview={destinationPreview}
              dimensions={destinationDimensions}
              label="Destination Asset"
              accept={destinationType === 'Image' ? 'image/*' : 'video/*'}
              onFileUpload={onDestinationFileUpload}
              onDrop={onDestinationDrop}
              onDragOver={onDestinationDragOver}
              onRemove={onDestinationRemove}
              required
            />
          </S.FormGroup>
        </S.FormRow>
      )}

      {destinationType === 'Stream' && (
        <StreamMetadataFields
          title="Destination Stream"
          details={streamMetadata}
          error={streamMetadataError}
          isLoading={streamMetadataLoading}
          onDurationChange={onStreamDurationChange}
          onUrlBlur={onStreamUrlBlur}
          onUrlChange={onStreamUrlChange}
          urlValue={streamUrl}
        />
      )}
    </>
  );
};
